/**
 * Format a `last_active_at` ISO timestamp as a human-readable
 * presence string for profile cards.
 *
 * < 5 min:   "Active now" (treated as currently online)
 * < 60 min:  "Last active Xm ago"
 * < 24 hr:   "Last active Xh ago"
 * < 7 days:  "Last active Xd ago"
 * else:      "Last active a while ago" (no specific time leaked)
 * null:      null (caller renders nothing)
 */
export function formatLastActive(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return null;
  const diffMs = Date.now() - then;
  if (diffMs < 0) return 'Active now';
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 5) return 'Active now';
  if (minutes < 60) return `Last active ${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Last active ${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `Last active ${days}d ago`;
  return 'Last active a while ago';
}

/**
 * True when the formatted string represents "currently online" (< 5 min).
 * Used by callers to choose between green and grey presence dots.
 */
export function isActiveNow(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return false;
  const diffMs = Date.now() - then;
  if (diffMs < 0) return true;
  return diffMs < 5 * 60_000;
}
